import { useEffect } from 'react'

/**
 * Locks scrolling on the host page while an overlay is open.
 *
 * Reference-counted, because a page may legitimately host two widgets and the
 * second one closing must not unlock the page while the first is still open.
 * The host's original inline style is restored exactly — some site themes set
 * their own overflow and would not survive being blanked.
 */
let lockCount = 0
let previousOverflow: string | null = null
let previousPaddingRight: string | null = null

function scrollbarWidth(): number {
  if (typeof window === 'undefined') return 0
  return Math.max(0, window.innerWidth - document.documentElement.clientWidth)
}

function lock(): void {
  lockCount += 1
  if (lockCount > 1) return

  const { body } = document
  previousOverflow = body.style.overflow
  previousPaddingRight = body.style.paddingRight

  // Compensate for the disappearing scrollbar so the host page does not jump.
  const gap = scrollbarWidth()
  if (gap > 0) {
    const current = parseFloat(window.getComputedStyle(body).paddingRight) || 0
    body.style.paddingRight = `${current + gap}px`
  }

  body.style.overflow = 'hidden'
}

function unlock(): void {
  if (lockCount === 0) return
  lockCount -= 1
  if (lockCount > 0) return

  const { body } = document
  body.style.overflow = previousOverflow ?? ''
  body.style.paddingRight = previousPaddingRight ?? ''
  previousOverflow = null
  previousPaddingRight = null
}

export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return
    lock()
    return unlock
  }, [active])
}

/** Test seam. */
export function resetScrollLockForTests(): void {
  lockCount = 0
  previousOverflow = null
  previousPaddingRight = null
  document.body.style.overflow = ''
  document.body.style.paddingRight = ''
}
